import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalHeader } from '@creditornot/cb-modal';
import { useAuthState } from '@creditornot/wolt-auth/react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { dismissNpsSurvey, fetchEligibleForNpsSurvey } from 'apis/merchant-gateway/nps/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { WoltModal } from 'common/components';
import { useMediaQuery } from 'common/hooks/useMediaQuery';

import AskLaterModalContent from './AskLaterModalContent';
import ConfirmationModalContent from './ConfirmationModalContent';
import SurveyModalContent from './SurveyModalContent';

type NpsProps = {
  venue: Venue;
  manual?: boolean;
  onClose?: () => void;
};

export const BREAKPOINT_MOBILE = 768;

type ModalContentType = 'nps' | 'ask-later' | 'confirmation';

const ModalContainer = styled.div`
  background-color: ${cssThemedValue('surfaceMain')};
  width: 50vw;
  border-radius: 8px;
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    width: 98vw;
  }
`;

const Nps = ({ venue, manual = false, onClose }: NpsProps) => {
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINT_MOBILE + 1}px)`);

  const [shouldShowNpsModal, setShouldShowNpsModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContentType>('confirmation');

  const { user } = useAuthState();
  const { data = { isUserEligibleForNpsSurvey: false }, isLoading } = useQuery(
    ['/merchant-analytics-api/users/current/eligible_for_nps_survey', venue.id],
    () =>
      fetchEligibleForNpsSurvey({
        venue_id: venue.id,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!user && !manual,
      retry: false,
    },
  );

  const { refetch: dismissReview } = useQuery(
    ['/merchant-analytics-api/surveys/nps/dismiss'],
    () => dismissNpsSurvey(),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  useEffect(() => {
    if (!isLoading) {
      const isEligibleForSurvey = data.isUserEligibleForNpsSurvey || manual;
      setShouldShowNpsModal(!isMobile && isEligibleForSurvey);
    }
  }, [data, isLoading, isMobile, manual]);

  const handleSubmissionSuccess = () => {
    closeModal();
    toast.success(<FormattedMessage id="nps.alerts.feedback-submitted" />);
  };

  const handleSubmissionFailure = () => {
    closeModal();
    toast.error(<FormattedMessage id="nps.alerts.feedback-fail" />);
  };

  const closeModal = () => {
    setShouldShowNpsModal(false);
    onClose && onClose();
  };

  return (
    <WoltModal isOpen={shouldShowNpsModal} onRequestClose={() => closeModal()}>
      <ModalContainer>
        <ModalHeader>
          <FormattedMessage id="nps.title" />
        </ModalHeader>

        {modalContent === 'ask-later' && <AskLaterModalContent onConfirm={() => closeModal()} />}

        {modalContent === 'nps' && (
          <SurveyModalContent
            onSubmissionSuccess={() => handleSubmissionSuccess()}
            onSubmissionError={() => handleSubmissionFailure()}
            venue={venue}
          />
        )}

        {modalContent === 'confirmation' && (
          <ConfirmationModalContent
            onConfirm={() => setModalContent('nps')}
            onLater={() => {
              setModalContent('ask-later');
              dismissReview();
            }}
          />
        )}
      </ModalContainer>
    </WoltModal>
  );
};

export default Nps;
